// src/components/MainMenuBusinessCard.js
import React from "react";
import { useNavigate } from "react-router-dom";
import ImageWithFallback from "./ImageWithFallback";
import SmartLogoFrame, { getBusinessLogo } from "./SmartLogoFrame";

const PUBLIC_NO_IMAGE = (process.env.PUBLIC_URL || "") + "/no-image.png";

export function isMobileApp() {
  return (
    typeof document !== "undefined" &&
    document.body &&
    document.body.classList &&
    document.body.classList.contains("is-mobile-app")
  );
}

function isAbsoluteUrl(url) {
  return !!url && (/^\w+:\/\//i.test(url) || /^data:/i.test(url) || /^file:/i.test(url));
}

function pickImage(biz) {
  return getBusinessLogo(biz);
}

function buildImageCandidates(url) {
  if (!url || typeof url !== "string") return [PUBLIC_NO_IMAGE];

  const trimmed = url.trim();

  if (isMobileApp()) {
    if (isAbsoluteUrl(trimmed)) return [trimmed, PUBLIC_NO_IMAGE];

    try {
      if (typeof window !== "undefined" && window.location && window.location.origin) {
        return [
          window.location.origin + (trimmed.startsWith("/") ? trimmed : "/" + trimmed),
          PUBLIC_NO_IMAGE,
        ];
      }
    } catch (_) {}

    return [trimmed, PUBLIC_NO_IMAGE];
  }

  const out = [trimmed];

  try {
    if (trimmed.startsWith("/") && typeof window !== "undefined" && window.location && window.location.origin) {
      out.push(window.location.origin + trimmed);
    }
  } catch (_) {}

  try {
    const publicUrl = process.env.PUBLIC_URL || "";
    if (publicUrl) {
      const stripped = trimmed.replace(/^\/+/, "");
      out.push((publicUrl.endsWith("/") ? publicUrl.slice(0, -1) : publicUrl) + "/" + stripped);
    }
  } catch (_) {}

  if (!out.includes(PUBLIC_NO_IMAGE)) out.push(PUBLIC_NO_IMAGE);

  return Array.from(new Set(out.filter(Boolean)));
}


export function getBusinessId(biz) {
  if (!biz || typeof biz !== "object") return "";
  const candidates = [
    biz.id,
    biz.businessId,
    biz._id,
    biz.uid,
    biz.slug,
    biz.business_id,
    biz.docId,
    biz.documentId,
  ];

  if (biz.ref && biz.ref.path) candidates.push(biz.ref.path);
  if (biz.path) candidates.push(biz.path);

  for (const value of candidates) {
    if (value == null) continue;
    let text = String(value).trim();
    if (!text || text === "undefined" || text === "null") continue;

    // Firestore paths can arrive as businesses/abc123. The route needs only abc123.
    if (text.includes("/")) {
      const parts = text.split("/").filter(Boolean);
      text = parts[parts.length - 1] || text;
    }

    if (text) return text;
  }

  return "";
}

function getName(biz) {
  return biz?.name || biz?.businessName || biz?.title || "Negocio";
}

function getCategory(biz) {
  const raw =
    biz?.categoryName ||
    biz?.businessTypeLabel ||
    biz?.typeLabel ||
    biz?.category ||
    biz?.businessType ||
    "Destacado";

  const text = String(raw || "Destacado").trim();

  const map = {
    beauty_salon: "Belleza",
    barber: "Belleza",
    barberia: "Belleza",
    fitness: "Gimnasio",
    gym: "Gimnasio",
    spa: "Spa",
    wellness: "Spa",
    home: "Hogar",
    hogar: "Hogar",
  };

  return map[text] || (text.length > 14 ? "Destacado" : text.charAt(0).toUpperCase() + text.slice(1));
}


export function getLocationShort(biz) {
  const value = biz?.city || biz?.cityName || biz?.locationCity || biz?.district || biz?.sector || biz?.address || "";
  if (typeof value !== "string") return "";
  const seen = new Set();
  return value.split(",").map(part => part.trim()).filter(part => {
    const key = part.toLocaleLowerCase("es");
    if (!key || seen.has(key)) return false;
    seen.add(key); return true;
  }).join(", ");
}

function normalizeDistanceValue(value) {
  if (value == null || value === "") return "";

  if (typeof value === "string") {
    const lower = value.toLowerCase();
    if (lower.includes("km") || lower.includes("m de ti") || lower.includes("metros")) return value;

    const numeric = Number(value);
    if (!Number.isNaN(numeric)) return normalizeDistanceValue(numeric);

    return value;
  }

  const n = Number(value);
  if (!Number.isFinite(n)) return "";

  if (n < 1) return `${Math.round(n * 1000)} m`;
  if (n < 10) return `${n.toFixed(1)} km`;
  return `${Math.round(n)} km`;
}

export function getDistanceText(biz, distances = {}) {
  if (!biz) return "";

  const keys = [biz.id, biz.businessId, biz._id, biz.uid, biz.slug].filter(Boolean);

  for (const key of keys) {
    if (distances && Object.prototype.hasOwnProperty.call(distances, key)) {
      return normalizeDistanceValue(distances[key]);
    }
  }

  if (biz.distanceText) return biz.distanceText;
  if (biz.distanceKm != null) return normalizeDistanceValue(biz.distanceKm);
  if (biz.distance != null) return normalizeDistanceValue(biz.distance);

  return "";
}

function HeartIcon({ filled = false }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill={filled ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth="2.15"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8Z" />
    </svg>
  );
}

export function MainMenuBusinessCardSkeleton() {
  const mobile = isMobileApp();
  return (
    <div
      className="loop-discover-skeleton"
      style={{
        minWidth: mobile ? 176 : 330,
        maxWidth: mobile ? 176 : 330,
        height: mobile ? 114 : 204,
        borderRadius: mobile ? 24 : 30,
        background: "linear-gradient(90deg, #f1f5f9 0%, #f8fafc 50%, #f1f5f9 100%)",
        backgroundSize: "200% 100%",
        animation: "loopDiscoverShimmer 1.25s infinite linear",
      }}
    />
  );
}

export default function MainMenuBusinessCard({
  biz,
  isFavorite = false,
  onToggleFavorite,
  onOpenBusiness,
  distanceText = "",
}) {
  const navigate = useNavigate();
  const mobile = isMobileApp();
  const name = getName(biz);
  const category = getCategory(biz);
  const locationShort = getLocationShort(biz);
  const image = pickImage(biz) || PUBLIC_NO_IMAGE;
  const candidates = buildImageCandidates(image);
  const src = candidates[0];
  const extra = candidates.slice(1);

  const businessId = getBusinessId(biz);

  const openBusiness = (event) => {
    if (event && typeof event.preventDefault === "function") event.preventDefault();
    if (event && typeof event.stopPropagation === "function") event.stopPropagation();

    const normalizedBiz = { ...(biz || {}), id: businessId || biz?.id || biz?.businessId || biz?._id };

    if (typeof onOpenBusiness === "function") {
      onOpenBusiness(normalizedBiz);
      return;
    }

    if (businessId) {
      navigate(`/business/${encodeURIComponent(businessId)}`);
      return;
    }

    console.warn("[MainMenuBusinessCard] Missing business id for navigation", biz);
  };

  const toggleFavorite = (event) => {
    event.stopPropagation();
    if (typeof onToggleFavorite === "function") {
      onToggleFavorite(businessId || biz?.id || biz?.businessId || biz?._id, biz);
    }
  };

  const cardWidth = mobile ? 176 : 330;
  const cardHeight = mobile ? 114 : 204;

  return (
    <article
      className="loop-discover-card"
      title={name}
      style={{
        cursor: "pointer",
        touchAction: "manipulation",
        minWidth: cardWidth,
        maxWidth: cardWidth,
        height: cardHeight,
        borderRadius: mobile ? 18 : 24,
        overflow: "hidden",
        background: "#f4eadc",
        border: "1px solid rgba(255,248,238,0.72)",
        boxShadow: mobile ? "0 16px 42px rgba(54,41,25,0.11)" : "0 26px 72px rgba(54,41,25,0.15)",
        position: "relative",
        transition: "transform 180ms ease, box-shadow 180ms ease, filter 180ms ease",
        scrollSnapAlign: "start",
        transform: "translateZ(0)",
      }}
    >
      <style>{`
        .loop-card-bg {
          background-size: cover !important;
          background-repeat: no-repeat !important;
          background-position: center center !important;
          transform: scale(1.03) !important;
          filter: saturate(1.02) contrast(1.01) !important;
        }

        .loop-discover-card:hover {
          transform: translateY(-4px) translateZ(0);
          box-shadow: 0 34px 88px rgba(15,23,42,0.20);
          filter: saturate(1.03);
        }

        .loop-discover-card:active {
          transform: scale(0.985) translateZ(0);
        }

        .loop-discover-card .loop-card-bg {
          transition: transform 280ms ease, filter 280ms ease;
        }

        .loop-discover-card:hover .loop-card-bg {
          transform: scale(1.07);
          filter: saturate(1.03);
        }

        @keyframes loopDiscoverShimmer {
          from { background-position: 200% 0; }
          to { background-position: -200% 0; }
        }

        @media (max-width: 768px) {
          .loop-discover-card:hover {
            transform: none;
          }

          .loop-discover-card:hover .loop-card-bg {
            transform: scale(1.04);
          }
        }
      `}</style>

      {/* Invisible loader keeps ImageWithFallback candidate/fallback behavior without controlling layout. */}
      <ImageWithFallback
        src={src}
        candidates={extra}
        fallback={PUBLIC_NO_IMAGE}
        alt={name}
        style={{
          position: "absolute",
          width: 0,
          height: 0,
          opacity: 0,
          pointerEvents: "none",
        }}
      />

      {/* Visual image: Main Menu cards keep the original full-bleed cover look. */}
      <div
        className="loop-card-bg"
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `url("${src}")`,
          backgroundSize: "cover",
          backgroundPosition: "center center",
          backgroundRepeat: "no-repeat",
          transform: "scale(1.04)",
          pointerEvents: "none",
        }}
      />

      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(180deg, rgba(20,17,13,0.08) 0%, rgba(20,17,13,0.02) 42%, rgba(20,17,13,0.62) 100%)",
          pointerEvents: "none",
        }}
      />

      {/* Bottom business info */}
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 2,
          padding: mobile ? "11px 10px 9px" : "16px 16px 14px",
          background:
            "linear-gradient(180deg, rgba(20,17,13,0.00) 0%, rgba(20,17,13,0.16) 28%, rgba(20,17,13,0.76) 100%)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            color: "#ffffff",
            fontSize: mobile ? 13.5 : 21,
            fontWeight: 900,
            lineHeight: 1.05,
            letterSpacing: "-0.04em",
            textShadow: "0 2px 12px rgba(0,0,0,0.35)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "normal",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            maxWidth: "92%",
          }}
        >
          {name}
        </div>

        <div
          style={{
            marginTop: mobile ? 3 : 6,
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          {locationShort ? (
            <>
              <span
                style={{
                  color: "rgba(255,255,255,0.82)",
                  fontSize: mobile ? 9.4 : 12,
                  fontWeight: 700,
                }}
              >
                {locationShort}
              </span>
            </>
          ) : null}

          {distanceText ? (
            <>
              <span
                style={{
                  width: 4,
                  height: 4,
                  borderRadius: 999,
                  background: "rgba(255,255,255,0.55)",
                }}
              />

              <span
                style={{
                  color: "rgba(255,255,255,0.82)",
                  fontSize: mobile ? 9.4 : 12,
                  fontWeight: 700,
                }}
              >
                {distanceText}
              </span>
            </>
          ) : null}
        </div>
      </div>


      <button
        type="button"
        aria-label={`Abrir ${name}`}
        onClick={openBusiness}
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 1,
          border: 0,
          background: "transparent",
          padding: 0,
          margin: 0,
          cursor: "pointer",
        }}
      />

      <span
        style={{
          position: "absolute",
          zIndex: 2,
          pointerEvents: "none",
          left: mobile ? 8 : 12,
          top: mobile ? 8 : 12,
          borderRadius: 999,
          padding: mobile ? "5px 7px" : "7px 10px",
          background: "rgba(15,23,42,0.56)",
          color: "#ffffff",
          fontSize: mobile ? 9 : 11.5,
          fontWeight: 950,
          lineHeight: 1,
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          maxWidth: "70%",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          boxShadow: "0 8px 20px rgba(15,23,42,0.16)",
        }}
      >
        {category}
      </span>

      {typeof onToggleFavorite === "function" ? (
        <button
          type="button"
          aria-label={`${isFavorite ? "Quitar favorito" : "Agregar favorito"}: ${name}`}
          aria-pressed={isFavorite}
          onClick={toggleFavorite}
          style={{
            position: "absolute",
            zIndex: 3,
            right: mobile ? 8 : 12,
            top: mobile ? 8 : 12,
            width: mobile ? 30 : 38,
            height: mobile ? 30 : 38,
            borderRadius: 999,
            border: "1px solid rgba(255,255,255,0.46)",
            background: "rgba(15,23,42,0.38)",
            color: isFavorite ? "#fbbf24" : "#ffffff",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            boxShadow: "0 8px 20px rgba(15,23,42,0.16)",
          }}
        >
          <HeartIcon filled={isFavorite} />
        </button>
      ) : null}
    </article>
  );
}
